/**
 * Sentry facade — dynamically imports @sentry/react only when a DSN is
 * configured, keeping ~25-30 KB gzip out of the main bundle otherwise.
 *
 * Exports a lightweight `Sentry` shim that ErrorBoundary (and anything
 * else) can call synchronously.  Before the real SDK loads (or when no
 * DSN is set) every method is a silent no-op.
 */

// The real Sentry module, populated after dynamic import resolves.
let _sentry = null;

// No-op stand-in so callers never have to null-check.
const noop = () => {};
const noopScope = { setTag: noop, setContext: noop };

const SentryShim = new Proxy(
  {},
  {
    get(_target, prop) {
      if (_sentry) return _sentry[prop];
      // Provide safe no-ops for the methods ErrorBoundary uses.
      if (prop === 'withScope') return (cb) => cb(noopScope);
      if (prop === 'captureException') return noop;
      if (prop === 'init') return noop;
      return noop;
    },
  }
);

/**
 * Initialize Sentry for the frontend.
 *
 * DSN is read from REACT_APP_SENTRY_DSN (baked in at build time).
 * If unset, Sentry is never loaded — zero cost.
 *
 * Get a DSN by creating a free project at https://sentry.io/signup/
 * (free tier: 5k errors/month, 1 user).
 */
export async function initSentry() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;

  if (!dsn) {
    // No DSN configured — skip loading entirely.
    return;
  }

  const Sentry = await import('@sentry/react');
  _sentry = Sentry;

  Sentry.init({
    dsn,
    environment: process.env.NODE_ENV || 'development',
    release: process.env.REACT_APP_SENTRY_RELEASE || undefined,

    // Capture 10% of transactions for performance monitoring in prod,
    // 100% in dev. Adjust down if quota is tight.
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.1 : 1.0,

    // Only send errors in production by default. Set
    // REACT_APP_SENTRY_ENABLE_DEV=true to override locally.
    enabled:
      process.env.NODE_ENV === 'production' ||
      process.env.REACT_APP_SENTRY_ENABLE_DEV === 'true',

    // Filter out noisy, non-actionable errors.
    ignoreErrors: [
      // ResizeObserver loop warnings — harmless browser quirk
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Network offline / aborted fetches — user connectivity, not our bug
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      'AbortError',
      'The operation was aborted',
      // Browser extension noise
      'Non-Error promise rejection captured',
      'top.GLOBALS',
      // Safari/iOS quirks
      'cancelled',
      'TypeError: cancelled',
    ],
    denyUrls: [
      // Browser extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
      /^safari-extension:\/\//i,
      // Google Maps / third-party scripts we don't control
      /maps\.googleapis\.com/i,
      /maps\.gstatic\.com/i,
    ],

    beforeSend(event, hint) {
      // Drop events with no stack trace (usually injected scripts)
      const error = hint && hint.originalException;
      if (error && typeof error === 'object' && !error.stack) {
        return null;
      }
      return event;
    },
  });
}

export { SentryShim as Sentry };
